import {
  type ColumnDef,
  type ColumnFiltersState,
  type FilterFn,
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowDown, ArrowUp, Search } from 'lucide-react';
import { Fragment, useEffect, useMemo, useState } from 'react';

import useDebounce from '@/shared/hooks/useDebounce';

import { Pagination } from './Pagination';

const LOADING_ROWS_COUNT = 5;

/** Server toolbar search debounce (ms); reuse for custom search UIs that are not `TanStackTable` (same delay as toolbar search). */
export const SERVER_TABLE_SEARCH_DEBOUNCE_MS = 500;

function getFirstSortableColumnId<TData>(cols: ColumnDef<TData>[]): string | undefined {
  const stack: ColumnDef<TData>[] = [...cols];
  while (stack.length > 0) {
    const c = stack.shift();
    if (!c) {
      break;
    }
    // Depth-first: if column groups exist, inspect their children first.
    const maybeGroup = c as unknown as { columns?: ColumnDef<TData>[] };
    if (maybeGroup.columns && maybeGroup.columns.length > 0) {
      stack.unshift(...maybeGroup.columns);
      continue;
    }
    const id =
      (c as unknown as { id?: string }).id ??
      (c as unknown as { accessorKey?: string }).accessorKey;
    if (!id) {
      continue;
    }
    if (id === 'actions') {
      continue;
    }
    return String(id);
  }
  return undefined;
}

/** Tooltip text for truncated cells (native `title`); avoids huge JSON blobs. */
function formatCellValueForTitle(value: unknown): string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  if (typeof value === 'string') {
    return value.trim() === '' ? undefined : value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (typeof value === 'object') {
    try {
      const s = JSON.stringify(value);
      return s.length > 2000 ? `${s.slice(0, 2000)}…` : s;
    } catch {
      return undefined;
    }
  }
  return String(value);
}

export interface ITanStackTableProps<TData> {
  data: TData[];
  columns: ColumnDef<TData>[];
  loading?: boolean;
  emptyMessage?: string;
  enableSearch?: boolean;
  enablePagination?: boolean;
  pageSize?: number;
  className?: string;
  maxHeight?: string;
  /** Minimum height of the table body scroll area (few rows still looks balanced). */
  minHeight?: string;
  /** Pin first column (e.g. Test ID) so it stays visible when scrolling horizontally */
  stickyFirstColumn?: boolean;
  /** Optional filters (e.g. Status, Owner) rendered beside the search bar */
  customFilters?: React.ReactNode;
  /** Actions on the right of the toolbar (aligned with search on large screens). */
  toolbarEnd?: React.ReactNode;
  /** Content between the toolbar and the table card (e.g. info banners). */
  belowToolbar?: React.ReactNode;
  /** Content rendered at the very top of the card, above the toolbar, with a bottom border. */
  cardHeader?: React.ReactNode;
  /** Placeholder for the search input (default: "Search...") */
  searchPlaceholder?: string;
  /** Enable TanStack row selection (useful for bulk actions). */
  enableRowSelection?: boolean;
  /** Controlled rowSelection state (when provided). */
  rowSelection?: RowSelectionState;
  /** Called whenever row selection changes. */
  onRowSelectionChange?: (next: RowSelectionState) => void;
  /** Provide a stable row id (recommended when using selection). */
  getRowId?: (originalRow: TData, index: number, parent?: { id: string }) => string;
  /** Controlled current page (1-indexed). */
  currentPage?: number;
  /** Called when page changes (1-indexed). */
  onPageChange?: (page: number) => void;
  /**
   * Reset internal table UI state (search/sort/filters/selection/page) whenever `data` changes.
   * Useful when switching datasets (e.g. changing module) to avoid stale filters hiding rows.
   */
  resetStateOnDataChange?: boolean;
  /**
   * When true, the table does not reorder rows; data should already reflect API order.
   * Use `onServerSortChange` to translate TanStack `SortingState` into `sort_by` and refetch.
   */
  manualSorting?: boolean;
  onServerSortChange?: (sorting: SortingState) => void;
  /** When true with totalItems, pagination is server-driven (data is one page only). */
  serverSidePagination?: boolean;
  /** Total rows on server when using serverSidePagination. */
  totalItems?: number;
  /** When true with onServerSearchChange, search is handled by the parent (no client filtering). */
  serverSideSearch?: boolean;
  serverSearchValue?: string;
  onServerSearchChange?: (query: string) => void;
  globalFilterFn?: FilterFn<TData>;
  /** When set, clicking a row calls this handler (e.g. expand/collapse). */
  onRowClick?: (row: TData, rowId: string) => void;
  /** Row id (from getRowId) that is expanded below the row. */
  expandedRowId?: string | null;
  /** Renders content in a full-width row beneath the data row when expanded. */
  renderExpandedRow?: (row: TData) => React.ReactNode;
}

export function TanStackTable<TData>({
  data,
  columns,
  loading = false,
  emptyMessage = 'No data found',
  enableSearch = true,
  enablePagination = true,
  pageSize = 10,
  className = '',
  maxHeight,
  minHeight = 'min(55vh, 30rem)',
  stickyFirstColumn = false,
  customFilters,
  toolbarEnd,
  belowToolbar,
  cardHeader,
  searchPlaceholder = 'Search...',
  enableRowSelection = false,
  rowSelection: rowSelectionProp,
  onRowSelectionChange,
  getRowId,
  currentPage,
  onPageChange,
  resetStateOnDataChange = true,
  manualSorting = false,
  onServerSortChange,
  serverSidePagination = false,
  totalItems: serverTotalItems = 0,
  serverSideSearch = false,
  serverSearchValue = '',
  onServerSearchChange,
  globalFilterFn,
  onRowClick,
  expandedRowId,
  renderExpandedRow,
}: ITanStackTableProps<TData>) {
  const firstSortableColumnId = useMemo(() => getFirstSortableColumnId(columns), [columns]);
  const defaultSorting = useMemo<SortingState>(
    () => (firstSortableColumnId ? [{ id: firstSortableColumnId, desc: false }] : []),
    [firstSortableColumnId]
  );
  const serverPag =
    Boolean(serverSidePagination) &&
    typeof serverTotalItems === 'number' &&
    Number.isFinite(serverTotalItems);
  const serverPageCount =
    serverPag && enablePagination
      ? Math.max(1, Math.ceil(Math.max(0, serverTotalItems) / pageSize))
      : undefined;
  const columnsWithFirstSortingOnly = useMemo<ColumnDef<TData>[]>(() => {
    if (manualSorting) {
      return columns;
    }
    const apply = (col: ColumnDef<TData>): ColumnDef<TData> => {
      const maybeGroup = col as unknown as { columns?: ColumnDef<TData>[] };
      if (maybeGroup.columns && maybeGroup.columns.length > 0) {
        return { ...(col as object), columns: maybeGroup.columns.map(apply) } as ColumnDef<TData>;
      }
      const id =
        (col as unknown as { id?: string }).id ??
        (col as unknown as { accessorKey?: string }).accessorKey;
      const resolvedId = id ? String(id) : undefined;
      const enableSortingForThisCol =
        Boolean(firstSortableColumnId) && resolvedId === firstSortableColumnId;
      return { ...(col as object), enableSorting: enableSortingForThisCol } as ColumnDef<TData>;
    };
    return columns.map(apply);
  }, [columns, firstSortableColumnId, manualSorting]);

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState<SortingState>(defaultSorting);
  const [uncontrolledRowSelection, setUncontrolledRowSelection] = useState<RowSelectionState>({});
  const [uncontrolledPagination, setUncontrolledPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize,
  });

  const [serverSearchDraft, setServerSearchDraft] = useState(() => String(serverSearchValue ?? ''));
  const debouncedServerSearch = useDebounce(serverSearchDraft, SERVER_TABLE_SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    if (!serverSideSearch) {
      return;
    }
    setServerSearchDraft(String(serverSearchValue ?? ''));
  }, [serverSideSearch, serverSearchValue]);

  useEffect(() => {
    if (!serverSideSearch || !onServerSearchChange) {
      return;
    }
    const committed = String(serverSearchValue ?? '');
    if (debouncedServerSearch === committed) {
      return;
    }
    onServerSearchChange(debouncedServerSearch);
  }, [debouncedServerSearch, onServerSearchChange, serverSearchValue, serverSideSearch]);

  const resolvedRowSelection = rowSelectionProp ?? uncontrolledRowSelection;
  const isPaginationControlled =
    enablePagination &&
    typeof currentPage === 'number' &&
    Number.isFinite(currentPage) &&
    currentPage >= 1;
  const resolvedPagination = useMemo<PaginationState>(() => {
    if (!enablePagination) {
      return uncontrolledPagination;
    }
    if (isPaginationControlled) {
      return { ...uncontrolledPagination, pageIndex: currentPage - 1 };
    }
    return uncontrolledPagination;
  }, [currentPage, enablePagination, uncontrolledPagination, isPaginationControlled]);

  useEffect(() => {
    setUncontrolledPagination((prev) => ({ ...prev, pageSize }));
  }, [pageSize]);

  useEffect(() => {
    if (!resetStateOnDataChange) {
      return;
    }
    setGlobalFilter('');
    setColumnFilters([]);
    setColumnVisibility({});
    setUncontrolledRowSelection({});
    // If pagination is controlled by the parent (via `currentPage`), do not force-reset the page
    // on data refresh (e.g. after save + refetch). The parent is the source of truth.
    if (!isPaginationControlled) {
      setUncontrolledPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
    // For server-side sorting (`manualSorting`), data refreshes are expected after a sort change.
    // Resetting sorting here would overwrite the user's current sort and make the header icon flicker.
    if (!manualSorting) {
      setSorting(defaultSorting);
    }
  }, [data, resetStateOnDataChange, defaultSorting, isPaginationControlled, manualSorting]);

  useEffect(() => {
    // When columns change, `defaultSorting` may change (first sortable column id).
    // For client-side sorting we always follow it; for server-side sorting keep the user's choice.
    if (!manualSorting) {
      setSorting(defaultSorting);
      return;
    }
    // If manual sorting and we don't have a sorting set yet, initialize to default.
    setSorting((prev) => (prev.length === 0 ? defaultSorting : prev));
  }, [defaultSorting, manualSorting]);

  const table = useReactTable({
    data,
    columns: columnsWithFirstSortingOnly,
    getRowId,
    manualSorting,
    manualPagination: serverPag,
    pageCount: serverPageCount,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    ...(manualSorting ? {} : { getSortedRowModel: getSortedRowModel() }),
    getPaginationRowModel: enablePagination && !serverPag ? getPaginationRowModel() : undefined,
    enableSorting: true,
    // Avoid jumping back to page 1 on data refresh when pagination is controlled.
    // This is especially important after "save" actions that refetch the list.
    autoResetPageIndex: !isPaginationControlled && resetStateOnDataChange,
    // Keep the sort icon stable: toggles asc <-> desc (no "unsorted" state).
    enableSortingRemoval: false,
    enableRowSelection,
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: (updater) => {
      setSorting((prev) => {
        const next =
          typeof updater === 'function'
            ? (updater as (old: SortingState) => SortingState)(prev)
            : (updater as SortingState);
        if (manualSorting) {
          onServerSortChange?.(next);
        }
        return next;
      });
    },
    onRowSelectionChange: (updater) => {
      const next =
        typeof updater === 'function'
          ? updater(resolvedRowSelection)
          : (updater as RowSelectionState);
      if (rowSelectionProp === undefined) {
        setUncontrolledRowSelection(next);
      }
      onRowSelectionChange?.(next);
    },
    onPaginationChange: (updater) => {
      if (!enablePagination) {
        return;
      }
      setUncontrolledPagination((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        if (typeof currentPage !== 'number') {
          return next;
        }
        return { ...prev, pageSize: next.pageSize };
      });
      const next =
        typeof updater === 'function' ? updater(resolvedPagination) : (updater as PaginationState);
      onPageChange?.(next.pageIndex + 1);
    },
    globalFilterFn: globalFilterFn ?? 'includesString',
    state: {
      columnFilters,
      columnVisibility,
      globalFilter,
      sorting,
      rowSelection: resolvedRowSelection,
      pagination: resolvedPagination,
    },
  });

  // Make "Select all" behavior page-scoped when pagination is enabled.
  // Many callers wire header checkbox to `toggleAllRowsSelected`, which selects across all pages.
  // We remap those helpers to the page-only equivalents to avoid accidental "select everything".
  if (enablePagination && enableRowSelection) {
    const t = table as unknown as {
      getIsAllRowsSelected: () => boolean;
      getIsSomeRowsSelected: () => boolean;
      toggleAllRowsSelected: (value?: boolean) => void;
      getIsAllPageRowsSelected?: () => boolean;
      getIsSomePageRowsSelected?: () => boolean;
      toggleAllPageRowsSelected?: (value?: boolean) => void;
    };

    if (typeof t.getIsAllPageRowsSelected === 'function') {
      t.getIsAllRowsSelected = () => t.getIsAllPageRowsSelected?.() ?? false;
    }
    if (typeof t.getIsSomePageRowsSelected === 'function') {
      t.getIsSomeRowsSelected = () => t.getIsSomePageRowsSelected?.() ?? false;
    }
    if (typeof t.toggleAllPageRowsSelected === 'function') {
      t.toggleAllRowsSelected = (value?: boolean) => t.toggleAllPageRowsSelected?.(value);
    }
  }

  const getAlignClass = (align?: 'left' | 'center' | 'right') => {
    if (align === 'right') {
      return { text: 'text-right', justify: 'justify-end' };
    }
    if (align === 'center') {
      return { text: 'text-center', justify: 'justify-center' };
    }
    return { text: 'text-left', justify: 'justify-start' };
  };

  return (
    <div className={`w-full min-w-0 ${className}`}>
      {belowToolbar ? <div className='mb-4 min-w-0'>{belowToolbar}</div> : null}

      {/* Table + pagination: single card so footer reads as part of the data table */}
      <div className='w-full min-w-0 max-w-full rounded-2xl border border-gray-100 shadow-lg bg-white overflow-hidden flex flex-col'>
        {cardHeader ? (
          <div className='border-b border-gray-100 bg-white px-4 sm:px-6 py-4'>{cardHeader}</div>
        ) : null}
        {/* In-table toolbar (search + filters) */}
        {(enableSearch ||
          serverSideSearch ||
          customFilters ||
          toolbarEnd ||
          table.getHeaderGroups()[0]?.headers.some((h) => h.column.getCanFilter())) && (
          <div className='border-b border-gray-100 bg-white px-4 sm:px-6 py-3'>
            <div className='flex min-w-0 flex-col gap-3 lg:flex-row lg:items-center lg:gap-4'>
              <div className='flex min-w-0 flex-1 flex-wrap items-center gap-3 lg:flex-nowrap lg:gap-4'>
                {(enableSearch || serverSideSearch) && (
                  <div className='w-full min-w-0 lg:w-64 lg:max-w-xs lg:flex-shrink-0'>
                    <div className='relative'>
                      <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4' />
                      <input
                        value={serverSideSearch ? serverSearchDraft : (globalFilter ?? '')}
                        onChange={(e) => {
                          const v = String(e.target.value);
                          if (serverSideSearch) {
                            setServerSearchDraft(v);
                          } else {
                            setGlobalFilter(v);
                          }
                        }}
                        placeholder={searchPlaceholder}
                        className='w-full h-10 pl-10 pr-4 border border-gray-200 rounded-lg bg-white text-gray-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-theme-500 focus:border-transparent transition-all duration-200'
                      />
                    </div>
                  </div>
                )}
                {customFilters}
                {/* Column Filters (from column filterOptions) */}
                {table.getHeaderGroups()[0]?.headers.map((header) => {
                  if (!header.column.getCanFilter()) {
                    return null;
                  }
                  const column = header.column;
                  const columnDef = column.columnDef;
                  const filterOptions = (columnDef as { filterOptions?: string[] })?.filterOptions;

                  if (filterOptions && filterOptions.length > 0) {
                    return (
                      <div key={header.id} className='w-full min-w-0 sm:w-48'>
                        <select
                          value={(column.getFilterValue() as string) ?? ''}
                          onChange={(e) => {
                            const value = e.target.value;
                            column.setFilterValue(value === '' ? undefined : value);
                          }}
                          className='w-full h-10 px-4 border border-gray-200 rounded-lg bg-white text-gray-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-theme-500 focus:border-transparent transition-all duration-200'
                        >
                          <option value=''>All {columnDef.header as string}</option>
                          {filterOptions.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
              {toolbarEnd ? (
                <div className='flex w-full flex-shrink-0 flex-wrap items-center gap-2 lg:ml-auto lg:w-auto lg:justify-end'>
                  {toolbarEnd}
                </div>
              ) : null}
            </div>
          </div>
        )}

        <div
          className='w-full min-w-0 overflow-x-auto'
          style={{
            minHeight,
            ...(maxHeight
              ? { maxHeight, overflowY: 'auto' as const, WebkitOverflowScrolling: 'touch' }
              : { WebkitOverflowScrolling: 'touch' }),
          }}
        >
          <table
            className='w-full'
            style={{
              tableLayout: 'fixed',
              width: '100%',
              maxWidth: '100%',
              borderCollapse: 'separate',
              borderSpacing: 0,
              ...(loading ? { minHeight } : {}),
            }}
          >
            <thead className='sticky top-0 z-10'>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header, colIndex) => {
                    const isStickyLeft = stickyFirstColumn && colIndex === 0;
                    const isActionsColumn = header.column.id === 'actions';
                    const isTestIdColumn = header.column.id === 'identifier';
                    const meta = header.column.columnDef.meta as
                      | { width?: string; align?: 'left' | 'center' | 'right' }
                      | undefined;
                    const colWidth = meta?.width;
                    const align = isActionsColumn ? 'center' : meta?.align;
                    const alignClass = getAlignClass(align);
                    let headerMinWidth: number | undefined;
                    if (isStickyLeft) {
                      headerMinWidth = 100;
                    } else if (isActionsColumn) {
                      headerMinWidth = 160;
                    } else if (isTestIdColumn) {
                      headerMinWidth = 72;
                    }
                    return (
                      <th
                        key={header.id}
                        className={`px-4 sm:px-6 py-4 text-xs sm:text-sm font-bold text-black tracking-wider bg-theme-table-header ${
                          alignClass.text
                        } ${isTestIdColumn || isActionsColumn ? 'whitespace-nowrap' : ''}`}
                        style={{
                          width:
                            colWidth ?? (header.getSize() !== 150 ? header.getSize() : undefined),
                          minWidth: headerMinWidth,
                          backgroundColor: 'var(--theme-table-header-bg)',
                          position: 'sticky',
                          top: 0,
                          left: isStickyLeft ? 0 : undefined,
                          zIndex: isStickyLeft ? 20 : 10,
                        }}
                      >
                        {header.isPlaceholder ? null : (
                          <div className={`flex items-center gap-1 ${alignClass.justify}`}>
                            <span className='min-w-0'>
                              {flexRender(header.column.columnDef.header, header.getContext())}
                            </span>
                            {!isActionsColumn &&
                            header.column.getCanSort() &&
                            (manualSorting || header.column.id === firstSortableColumnId) ? (
                              <button
                                type='button'
                                className='inline-flex items-center justify-center rounded p-1 text-gray-700 hover:text-gray-900 hover:bg-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-500'
                                onClick={() => header.column.toggleSorting()}
                                aria-label={`Sort by ${String(
                                  header.column.columnDef.header ?? header.column.id
                                )}`}
                                title={
                                  header.column.getIsSorted() === 'desc'
                                    ? 'Sorted Z to A'
                                    : 'Sorted A to Z'
                                }
                              >
                                {(() => {
                                  const sorted = header.column.getIsSorted();
                                  if (sorted === 'asc') {
                                    return <ArrowUp className='h-4 w-4' />;
                                  }
                                  if (sorted === 'desc') {
                                    return <ArrowDown className='h-4 w-4' />;
                                  }
                                  return null;
                                })()}
                              </button>
                            ) : null}
                          </div>
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody className='bg-white dark:bg-transparent divide-y divide-gray-200 dark:divide-gray-700'>
              {loading &&
                Array.from({ length: LOADING_ROWS_COUNT }, (_, rowIndex) => (
                  <tr
                    key={`loading-${rowIndex}`}
                    className='hover:bg-gray-50 dark:[&>td]:border-b dark:[&>td]:border-gray-700'
                  >
                    {table.getVisibleLeafColumns().map((col) => {
                      const meta = col.columnDef.meta as
                        | {
                            width?: string;
                            noTruncate?: boolean;
                            align?: 'left' | 'center' | 'right';
                          }
                        | undefined;
                      const width = meta?.width;
                      const isActionsColumn = col.id === 'actions';
                      const isTestIdColumn = col.id === 'identifier';
                      const align = isActionsColumn ? 'center' : meta?.align;
                      const alignClass = getAlignClass(align);
                      return (
                        <td
                          key={`loading-${rowIndex}-${col.id}`}
                          className={`align-middle px-4 sm:px-6 py-2.5 text-sm sm:text-base leading-tight text-gray-900 ${
                            alignClass.text
                          } ${isTestIdColumn || isActionsColumn ? 'whitespace-nowrap' : ''}`}
                          style={{ width }}
                        >
                          <div className='h-6 bg-gray-200 rounded-md animate-pulse' />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              {!loading && table.getRowModel().rows.length === 0 && (
                <tr>
                  <td colSpan={table.getVisibleLeafColumns().length} className='p-0'>
                    <div
                      className='flex items-center justify-center px-4 py-12 text-center text-sm sm:text-base text-gray-500'
                      style={{ minHeight: `calc(${minHeight} - 2.75rem)` }}
                    >
                      {emptyMessage}
                    </div>
                  </td>
                </tr>
              )}
              {!loading &&
                table.getRowModel().rows.length > 0 &&
                table.getRowModel().rows.map((row, rowIndex) => (
                  <Fragment key={row.id}>
                    <tr
                      className={`transition-all duration-150 hover:bg-gray-100 dark:[&>td]:border-b dark:[&>td]:border-gray-700 ${
                        rowIndex % 2 === 1
                          ? 'bg-[#eaecec] dark:bg-transparent'
                          : 'bg-white dark:!bg-gray-800/40'
                      } ${onRowClick ? 'cursor-pointer' : ''}`}
                      onClick={
                        onRowClick
                          ? () => {
                              onRowClick(row.original, row.id);
                            }
                          : undefined
                      }
                      onKeyDown={
                        onRowClick
                          ? (event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                onRowClick(row.original, row.id);
                              }
                            }
                          : undefined
                      }
                      tabIndex={onRowClick ? 0 : undefined}
                      role={onRowClick ? 'button' : undefined}
                      aria-expanded={
                        renderExpandedRow && expandedRowId === row.id ? true : undefined
                      }
                    >
                      {row.getVisibleCells().map((cell, colIndex) => {
                        const isStickyLeft = stickyFirstColumn && colIndex === 0;
                        const isTestIdColumn = cell.column.id === 'identifier';
                        const isActionsColumn = cell.column.id === 'actions';
                        const rowBgClass =
                          rowIndex % 2 === 1
                            ? 'bg-[#eaecec] dark:bg-transparent'
                            : 'bg-white dark:!bg-gray-800/40';
                        const meta = cell.column.columnDef.meta as
                          | {
                              width?: string;
                              noTruncate?: boolean;
                              align?: 'left' | 'center' | 'right';
                            }
                          | undefined;
                        const colWidth = meta?.width;
                        const noTruncate = meta?.noTruncate ?? false;
                        const align = isActionsColumn ? 'center' : meta?.align;
                        const alignClass = getAlignClass(align);
                        const ellipsisClass =
                          isTestIdColumn || isActionsColumn || noTruncate
                            ? ''
                            : 'overflow-hidden text-ellipsis whitespace-nowrap';
                        const truncateTitle = formatCellValueForTitle(cell.getValue());
                        const cellContent = flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        );
                        let cellMinWidth: number | undefined;
                        if (isStickyLeft) {
                          cellMinWidth = 100;
                        } else if (isActionsColumn) {
                          cellMinWidth = 160;
                        } else if (isTestIdColumn) {
                          cellMinWidth = 72;
                        }
                        return (
                          <td
                            key={cell.id}
                            className={`px-4 sm:px-6 py-1 text-sm sm:text-base leading-tight text-gray-900 ${
                              alignClass.text
                            } ${isStickyLeft ? rowBgClass : ''} ${ellipsisClass} ${
                              isActionsColumn ? 'overflow-visible' : ''
                            } ${isTestIdColumn || isActionsColumn ? 'whitespace-nowrap' : ''}`}
                            style={{
                              width: colWidth ?? undefined,
                              position: isStickyLeft ? ('sticky' as const) : undefined,
                              left: isStickyLeft ? 0 : undefined,
                              zIndex: isStickyLeft ? 5 : undefined,
                              boxShadow: isStickyLeft ? '2px 0 4px rgba(0,0,0,0.06)' : undefined,
                              minWidth: cellMinWidth,
                            }}
                          >
                            {isTestIdColumn || isActionsColumn || noTruncate ? (
                              cellContent
                            ) : (
                              <span
                                className='block min-w-0 cursor-default truncate'
                                title={truncateTitle}
                              >
                                {cellContent}
                              </span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                    {renderExpandedRow && expandedRowId === row.id ? (
                      <tr className='bg-white dark:bg-transparent'>
                        <td
                          colSpan={table.getVisibleLeafColumns().length}
                          className='p-0 border-b border-gray-200 dark:border-gray-700'
                        >
                          {renderExpandedRow(row.original)}
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
            </tbody>
          </table>
        </div>

        {enablePagination && (
          <Pagination
            className='flex-shrink-0 bg-white rounded-b-2xl'
            currentPage={table.getState().pagination.pageIndex + 1}
            totalPages={
              serverPag && serverPageCount !== undefined ? serverPageCount : table.getPageCount()
            }
            totalItems={serverPag ? serverTotalItems : table.getFilteredRowModel().rows.length}
            itemsPerPage={table.getState().pagination.pageSize}
            onPageChange={(page) => {
              if (onPageChange) {
                onPageChange(page);
              }
              table.setPageIndex(page - 1);
            }}
            showEntriesInfo={true}
            showPageInfo={true}
          />
        )}
      </div>
    </div>
  );
}
